/**
 * Dev Test View Component - For local development and testing
 *
 * This component provides a standalone testing environment for the agent chat UI.
 * It includes a hardcoded catalog of test agents that mirrors the agents available
 * in the eureka-agent-executor/agents directory.
 *
 * Usage: Access via /api/flows-alpha-agent/ui/ when running with DEV_MODE=true
 */

import React, { useEffect, useState, useMemo } from 'react';
import { AgentChatPage, AgentInfo } from '@/components/AgentChatPage';
import { initAuth } from '@/lib/auth';
import { setIdentity, isIdentitySet } from '@/lib/config/services';

// Import styles
import '@/styles/globals.css';

/**
 * Hardcoded test catalog - mirrors agents in eureka-agent-executor/agents/
 * This allows testing without a backend catalog API
 */
const TEST_CATALOG: AgentInfo[] = [
  // ── Demo ──────────────────────────────────────────────────────────────
  {
    agent_id: 'the_guide',
    name: 'The Guide',
    category: 'demo',
    icon: '🧭',
    short_description: "Don't build systems for humans and add AI on top. Build systems for AI, and let AI serve the humans.",
    long_description: "Your AI guide to the world's first AI-native enterprise platform. I'm just a prompt — about 40 lines of English — running on the same platform I'm here to present.",
    capabilities: ['Voice narration', 'Architecture deep-dives', 'Interactive Q&A', 'AI-native manifesto'],
  },

  // ── Data Operations ───────────────────────────────────────────────────
  {
    agent_id: 'data_operations/data_discovery',
    name: 'Data Discovery',
    category: 'data',
    icon: '🔎',
    short_description: 'Interactive data exploration with ERD',
    long_description: 'Interactive data exploration agent. Discovers schema structure, generates ERD, and lets users explore data through sample viewing and natural language queries.',
    capabilities: ['ERD generation', 'Sample data', 'Natural language queries', 'Schema discovery'],
  },
  {
    agent_id: 'data_operations/data_profiler',
    name: 'Data Profiler',
    category: 'data',
    icon: '📊',
    short_description: 'Profile data tables and columns',
    long_description: 'Conversational agent for data profiling. Works with metadata-registered tables. Guides user through schema, table selection, and profiling.',
    capabilities: ['Column profiling', 'Statistics', 'Data quality insights'],
  },
  {
    agent_id: 'data_operations/ontology_extractor',
    name: 'Ontology Extractor',
    category: 'data',
    icon: '🧬',
    short_description: 'Build business ontology from data',
    long_description: 'Build business ontology from profiled data. Discovers entities, validates relationships, detects PII.',
    capabilities: ['Entity discovery', 'Relationship validation', 'PII detection', 'Ontology building'],
  },

  // ── System agents ─────────────────────────────────────────────────────
  {
    agent_id: 'system/troubleshooter',
    name: 'Troubleshooter',
    category: 'system',
    icon: '🔧',
    short_description: 'Triage and diagnose platform issues',
    long_description: 'Intelligent triage agent that analyzes symptoms and routes to specialized diagnostic subagents for workflows, queues, latency, and infrastructure.',
    capabilities: ['Workflow debugging', 'Queue analysis', 'Latency diagnosis', 'Infrastructure debugging'],
  },
  {
    agent_id: 'system/creator',
    name: 'Creator',
    category: 'system',
    icon: '🎨',
    short_description: 'Convert explorations into reusable agents',
    long_description: 'Conversational agent that helps users convert explorations into reusable agent definitions. Works like a pair programmer.',
    capabilities: ['Agent creation', 'YAML generation', 'Prompt engineering', 'Agent publishing'],
  },
  {
    agent_id: 'system/operator',
    name: 'Operator',
    category: 'system',
    icon: '⚙️',
    short_description: 'Agent scheduling, monitoring, and alerting',
    long_description: 'Manages agent operations including scheduling, monitoring, and alerting. Acts as the operations center for deployed agents.',
    capabilities: ['Agent scheduling', 'Monitoring', 'Alerting', 'Operations management'],
  },
];

/**
 * Default test user for dev mode
 */
const DEV_USER = {
  email: 'dev@test.local',
  name: 'Dev User',
  tenant_id: 'demo',
};

const DEV_PROJECT_ID = 'dev-project';

export interface DevTestViewProps {
  /**
   * Initial agent to select (optional)
   */
  initialAgentId?: string;
}

/**
 * DevTestView Component
 *
 * Standalone test environment with hardcoded catalog.
 * Used for local development without backend catalog API.
 */
export const DevTestView: React.FC<DevTestViewProps> = ({
  initialAgentId,
}) => {
  const [identityReady, setIdentityReady] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(
    initialAgentId
      ? TEST_CATALOG.find(a => a.agent_id === initialAgentId) || TEST_CATALOG[0]
      : TEST_CATALOG[0]
  );

  // Initialize identity
  useMemo(() => {
    setIdentity({
      tenantId: DEV_USER.tenant_id,
      projectId: DEV_PROJECT_ID,
      userId: DEV_USER.email,
    });
    console.log('DevTestView - Identity set for dev mode');
  }, []);

  useEffect(() => {
    if (isIdentitySet()) {
      setIdentityReady(true);
    }
  }, []);

  // Initialize auth in dev mode (no token required)
  useEffect(() => {
    initAuth({
      mode: 'none',
    });
  }, []);

  const handleAgentChange = (agent: AgentInfo) => {
    setSelectedAgent(agent);
    console.log('DevTestView - Agent changed:', agent.agent_id);
  };

  if (!identityReady) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          color: '#666',
        }}
      >
        Initializing dev environment...
      </div>
    );
  }

  const apiBaseUrl = '/api/flows-alpha-agent';
  const mcpBaseUrl = '/api/flows-alpha-agent';

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a',
      }}
    >
      <AgentChatPage
        agentId={selectedAgent?.agent_id}
        agentName={selectedAgent?.name || 'Agent'}
        agentDescription={selectedAgent?.short_description}
        agentCapabilities={selectedAgent?.capabilities}
        agentExamples={selectedAgent?.usage_examples}
        agentIcon={selectedAgent?.icon}
        projectId={DEV_PROJECT_ID}
        tenantId={DEV_USER.tenant_id}
        userId={DEV_USER.email}
        apiBaseUrl={apiBaseUrl}
        mcpBaseUrl={mcpBaseUrl}
        showHistory={true}
        availableAgents={TEST_CATALOG}
        onAgentChange={handleAgentChange}
        forceShowAgentList={true}
      />
    </div>
  );
};

export default DevTestView;
